import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';

import {
  CharterDetailsGetRes,
  CharterReservationReqBody,
  charterReservationUpdate,
  fetchCharterDetails,
} from '~/services/CharterService';

import {
  CharterDetails,
  CharterFlight,
  SeatInfo,
  SeatLegendItem,
  SeatSelectionState,
  Traveller,
} from '../charter.types';
import { deriveCharterDetails } from '../common/deriveCharterDetails';
import { determineSeatAvailability } from '../common/determineSeatAvailability';
import { generateSeatLookup } from '../common/generateSeatLookup';
import { generateSeatMap } from '../common/generateSeatMap';
import { seatLegend } from '../common/seatLegend';

export type UseCharterSeatManagementParams = {
  initialDetails?: CharterDetails;
  initialPnr?: string;
};

export type UseCharterSeatManagementReturn = {
  pnr: string;
  setPnr: (value: string) => void;
  charterDetails: CharterDetails | null;
  seatSelections: SeatSelectionState;
  isSearching: boolean;
  searchError: string | null;
  seatPaymentCollected: boolean;
  setSeatPaymentCollected: (value: boolean) => void;
  seatPaymentReference: string;
  setSeatPaymentReference: (value: string) => void;
  isSaving: boolean;
  saveError: string | null;
  saveConfirmation: string | null;
  handleSearch: () => void;
  handleSeatChange: (travellerId: string, flightId: string, seatId?: string) => void;
  availableSeatOptions: (traveller: Traveller, flight: CharterFlight, travellers: Array<Traveller>) => Array<SeatInfo>;
  handleSave: () => void;
  seatPriceForSelection: (flightId: string, seatId?: string) => number | undefined;
  seatLegendItems: Array<SeatLegendItem>;
  selectedSeatsPrices: Array<number>;
  getSelectionError: (traveller: Traveller, flight: CharterFlight) => string | null;
  hasInvalidSelections: boolean;
};

export const useCharterSeatManagement = ({
  initialDetails,
  initialPnr,
}: UseCharterSeatManagementParams = {}): UseCharterSeatManagementReturn => {
  const normalisedInitialPnr = useMemo(() => initialPnr?.trim().toUpperCase() ?? '', [initialPnr]);

  const [pnr, setPnr] = useState(normalisedInitialPnr);
  const [activePnr, setActivePnr] = useState<string | null>(normalisedInitialPnr || null);
  const [charterDetails, setCharterDetails] = useState<CharterDetails | null>(null);
  const [seatSelections, setSeatSelections] = useState<SeatSelectionState>({});
  const [seatPaymentCollected, setSeatPaymentCollected] = useState(false);
  const [seatPaymentReference, setSeatPaymentReference] = useState('');
  const [saveConfirmation, setSaveConfirmation] = useState<string | null>(null);
  const [clientValidationError, setClientValidationError] = useState<string | null>(null);

  const seatLookup = useMemo(() => {
    const flights = charterDetails?.flights ?? initialDetails?.flights;
    if (!flights) {
      return {} as Record<string, Record<string, SeatInfo>>;
    }

    const byFlight: Record<string, Record<string, SeatInfo>> = {};
    flights.forEach((flight) => {
      const rows = flight.cabinLayout.length ? flight.cabinLayout : generateSeatMap(flight.seatConfiguration);
      byFlight[flight.id] = generateSeatLookup(rows);
    });
    return byFlight;
  }, [charterDetails, initialDetails]);

  const {
    data,
    isFetching,
    error: searchErrorRaw,
    refetch,
  } = useQuery<CharterDetailsGetRes['result']>({
    queryKey: ['charterSeats', activePnr],
    queryFn: async () => {
      if (!activePnr) {
        throw new Error('PNR is required');
      }
      return await fetchCharterDetails(activePnr);
    },
    enabled: activePnr !== null,
    staleTime: 0,
  });

  useEffect(() => {
    if (!normalisedInitialPnr) {
      return;
    }

    setPnr(normalisedInitialPnr);
    setActivePnr(normalisedInitialPnr);
  }, [normalisedInitialPnr]);

  useEffect(() => {
    if (!data) {
      return;
    }

    const charterDetails = deriveCharterDetails(data);

    setCharterDetails(charterDetails);
    setSeatSelections(data.seatSelections ?? {});
    setSeatPaymentCollected(data.seatPaymentCollected);
    setSeatPaymentReference(data.seatPaymentReference ?? '');
    setClientValidationError(null);
    setSaveConfirmation(null);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: async (payload: CharterReservationReqBody & { pnr: string }) => await charterReservationUpdate(payload),
    onSuccess: (response) => {
      setSaveConfirmation(response.message);
    },
    onError: () => {
      setSaveConfirmation(null);
    },
  });

  const handleSearch = useCallback(() => {
    const trimmed = pnr.trim().toUpperCase();
    if (!trimmed) {
      return;
    }

    setActivePnr((prev) => {
      if (prev === trimmed) {
        void refetch();
        return prev;
      }

      return trimmed;
    });
  }, [pnr, refetch]);

  const handleSeatChange = useCallback(
    (travellerId: string, flightId: string, seatId?: string) => {
      setSeatSelections((prev) => {
        // apply the changed selection
        let next: SeatSelectionState = {
          ...prev,
          [flightId]: {
            ...prev[flightId],
            [travellerId]: seatId ?? null,
          },
        };

        // clear dependent child seats that no longer satisfy adjacency rules
        const flight = charterDetails?.flights.find((f) => f.id === flightId);
        const travellers = charterDetails?.travellers ?? [];

        if (flight && travellers.length) {
          const flightSelections = { ...(next[flightId] ?? {}) };

          travellers.forEach((t) => {
            const assignedSeatId = flightSelections[t.id] ?? null;
            if (!assignedSeatId) {
              return;
            }

            const seatInfo = seatLookup[flight.id]?.[assignedSeatId];
            if (!seatInfo) {
              return;
            }

            const isAllowed = determineSeatAvailability(
              seatInfo,
              t,
              travellers,
              next,
              flight.id,
              flight.seatConfiguration,
            );

            if (!isAllowed) {
              flightSelections[t.id] = null;
            }
          });

          next = {
            ...next,
            [flightId]: flightSelections,
          };
        }

        return next;
      });
      setClientValidationError(null);
      setSaveConfirmation(null);
    },
    [charterDetails, seatLookup],
  );

  const clearConfirmationAndSetSeatPaymentCollected = useCallback((value: boolean) => {
    setSeatPaymentCollected(value);
    setClientValidationError(null);
    setSaveConfirmation(null);
  }, []);

  const clearConfirmationAndSetSeatPaymentReference = useCallback((value: string) => {
    setSeatPaymentReference(value);
    setClientValidationError(null);
    setSaveConfirmation(null);
  }, []);

  const seatAssignedElsewhere = useCallback(
    (flightId: string, seatId: string, travellerId: string) => {
      const byTraveller = seatSelections[flightId] ?? {};
      return Object.entries(byTraveller).some(
        ([otherTravellerId, selectedSeat]) => otherTravellerId !== travellerId && selectedSeat === seatId,
      );
    },
    [seatSelections],
  );

  const availableSeatOptions = useCallback(
    (traveller: Traveller, flight: CharterFlight, travellers: Array<Traveller>) => {
      const rows = flight.cabinClass
        ? flight.cabinLayout.filter((row) => row.cabin === flight.cabinClass)
        : flight.cabinLayout;

      return rows
        .flatMap((row) => row.seats)
        .filter((seat) =>
          determineSeatAvailability(seat, traveller, travellers, seatSelections, flight.id, flight.seatConfiguration),
        )
        .filter((seat) => !seatAssignedElsewhere(flight.id, seat.seatId, traveller.id))
        .sort((a, b) => a.seatId.localeCompare(b.seatId, undefined, { numeric: true }));
    },
    [seatAssignedElsewhere, seatSelections],
  );

  const seatPriceForSelection = useCallback(
    (flightId: string, seatId?: string) => {
      if (!seatId) {
        return undefined;
      }

      return seatLookup[flightId]?.[seatId]?.price ?? 0;
    },
    [seatLookup],
  );

  const selectedSeatsPrices = useMemo(() => {
    return Object.entries(seatSelections).flatMap(([flightId, flightSelections]) =>
      Object.values(flightSelections)
        .filter((seatId): seatId is string => Boolean(seatId))
        .map((seatId) => seatLookup[flightId]?.[seatId]?.price ?? 0),
    );
  }, [seatSelections, seatLookup]);

  useEffect(() => {
    const allSeatsFree = selectedSeatsPrices.length > 0 && selectedSeatsPrices.every((price) => price === 0);

    if (allSeatsFree) {
      setSeatPaymentCollected(false);
      setSeatPaymentReference('');
    }
  }, [selectedSeatsPrices, setSeatPaymentCollected, setSeatPaymentReference]);

  const handleSave = useCallback(() => {
    if (!charterDetails) {
      return;
    }

    const allSeatsFree = selectedSeatsPrices.length > 0 && selectedSeatsPrices.every((price) => price === 0);

    const trimmedPaymentReference = seatPaymentReference.trim();
    if (seatPaymentCollected && !allSeatsFree && !trimmedPaymentReference) {
      setClientValidationError('Payment reference is required when payment is collected.');
      return;
    }

    // validate entire selection set via shared availability rules
    const travellers = charterDetails.travellers;
    for (const flight of charterDetails.flights) {
      const byTraveller = seatSelections[flight.id] ?? {};
      for (const t of travellers) {
        const assignedSeatId = byTraveller[t.id] ?? null;
        if (!assignedSeatId) {
          continue;
        }
        const seatInfo = seatLookup[flight.id]?.[assignedSeatId];
        if (!seatInfo) {
          continue;
        }
        const isAllowed = determineSeatAvailability(
          seatInfo,
          t,
          travellers,
          seatSelections,
          flight.id,
          flight.seatConfiguration,
        );
        if (!isAllowed) {
          setClientValidationError('Invalid seat selections: one or more seats violate seating rules.');
          return;
        }
      }
    }

    const payload: CharterReservationReqBody & { pnr: string } = {
      pnr: charterDetails.pnr,
      seatPaymentCollected: allSeatsFree ? false : seatPaymentCollected,
      seatPaymentReference: allSeatsFree ? undefined : trimmedPaymentReference,
      seatSelections: charterDetails.travellers.flatMap((traveller) =>
        charterDetails.flights.map((flight) => {
          const seatId = seatSelections[flight.id]?.[traveller.id] ?? undefined;
          const seatInfo = seatId ? seatLookup[flight.id]?.[seatId] : undefined;

          return {
            travellerId: traveller.id,
            flightInstanceId: flight.id,
            seatNumber: seatId,
            price: seatInfo?.price ?? 0,
          };
        }),
      ),
    };

    setSaveConfirmation(null);
    setClientValidationError(null);
    saveMutation.mutate(payload);
  }, [
    charterDetails,
    seatPaymentCollected,
    seatPaymentReference,
    saveMutation,
    seatLookup,
    seatSelections,
    selectedSeatsPrices,
  ]);

  const getSelectionError = useCallback(
    (traveller: Traveller, flight: CharterFlight): string | null => {
      const byTraveller = seatSelections[flight.id] ?? {};
      const seatId = byTraveller[traveller.id] ?? null;
      if (!seatId) {
        return null;
      }

      const seatInfo = seatLookup[flight.id]?.[seatId];
      if (!seatInfo) {
        return 'Invalid seat selection';
      }

      // Use shared rules first
      const allowed = determineSeatAvailability(
        seatInfo,
        traveller,
        charterDetails?.travellers ?? [],
        seatSelections,
        flight.id,
        flight.seatConfiguration,
      );
      if (allowed) {
        return null;
      }

      // Provide specific messages for known rules
      if (traveller.passengerType === 'INF') {
        return "Infants don't receive seat assignments.";
      }

      const seatLetter = seatId.slice(-1);
      const seatRow = seatId.slice(0, -1);
      const emergencySeatPartners = flight.seatConfiguration.emergencySeatPartners;
      const companionSeatLetter = emergencySeatPartners[seatLetter as keyof typeof emergencySeatPartners];
      const isEmergencyRowSeat = seatInfo.feature.isExitRow;
      const isEmergencySideSeat = isEmergencyRowSeat && Boolean(companionSeatLetter);

      if (traveller.passengerType === 'CHD' && isEmergencySideSeat) {
        const companionSeatId = `${seatRow}${companionSeatLetter}`;
        const hasAdultCompanion = (charterDetails?.travellers ?? [])
          .filter((t) => t.passengerType === 'ADT')
          .some((adult) => (seatSelections[flight.id]?.[adult.id] ?? null) === companionSeatId);
        if (!hasAdultCompanion) {
          return 'Child on emergency row side seat must have an adjacent adult.';
        }
      }

      const hasInfantInBooking = (charterDetails?.travellers ?? []).some((t) => t.passengerType === 'INF');
      if (traveller.passengerType === 'ADT' && hasInfantInBooking && isEmergencySideSeat) {
        return 'Adults cannot sit on emergency row side seats when an infant is in the booking.';
      }

      return 'Invalid seat selection';
    },
    [charterDetails, seatLookup, seatSelections],
  );

  const hasInvalidSelections = useMemo(() => {
    if (!charterDetails) {
      return false;
    }
    return charterDetails.travellers.some((traveller) =>
      charterDetails.flights.some((flight) => Boolean(getSelectionError(traveller, flight))),
    );
  }, [charterDetails, getSelectionError]);

  return {
    pnr,
    setPnr,
    charterDetails,
    seatSelections,
    isSearching: isFetching,
    searchError:
      searchErrorRaw instanceof Error ? searchErrorRaw.message : searchErrorRaw ? String(searchErrorRaw) : null,
    seatPaymentCollected,
    setSeatPaymentCollected: clearConfirmationAndSetSeatPaymentCollected,
    seatPaymentReference,
    setSeatPaymentReference: clearConfirmationAndSetSeatPaymentReference,
    isSaving: saveMutation.isPending,
    saveError:
      clientValidationError ??
      (saveMutation.error instanceof Error
        ? saveMutation.error.message
        : saveMutation.error
        ? String(saveMutation.error)
        : null),
    saveConfirmation,
    handleSearch,
    handleSeatChange,
    availableSeatOptions,
    handleSave,
    seatPriceForSelection,
    seatLegendItems: seatLegend,
    selectedSeatsPrices,
    getSelectionError,
    hasInvalidSelections,
  };
};
